import { useState, useEffect } from 'react';
import { Plus, Truck, Layers, Trash2, Archive, ArchiveRestore, Pencil, Hash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AdminDataTable, Column, BulkAction, Action } from '@/components/settings/AdminDataTable';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { IndustryAssignmentsDrawer } from '@/components/settings/IndustryAssignmentsDrawer';
import { ModelUnitsDrawer } from '@/components/settings/ModelUnitsDrawer';
import { CallsignsField } from '@/components/settings/CallsignsField';
import { CallsignsDirectory } from '@/components/settings/CallsignsDirectory';
import { callsignProblem, formatCallsigns, parseCallsigns } from '@/lib/callsigns';
import {
  getFilterIndustries,
  getFilterModels,
  createFilterIndustry,
  createFilterModel,
  updateFilterIndustry,
  updateFilterModel,
  deleteFilterIndustry,
} from '@/lib/adminApi';
import { toast } from 'react-toastify';
import type { FilterIndustry, FilterModel } from '@/types';

type FilterItem = FilterIndustry | FilterModel;
type Tab = 'industry' | 'model';

// Client-side mirrors of the server validators (api/routes/admin/filters.js +
// filterModels.js). The server is authoritative; these just avoid a round trip
// and give an inline message.
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const CODE_RE = /^[A-Z0-9]{2,6}$/;

interface DrawerState {
  mode: 'create' | 'edit';
  tab: Tab;
  item?: FilterItem;
}

export function SettingsFilters() {
  // 'callsigns' is a read-only directory, not a managed list, so it is not a Tab.
  const [activeTab, setActiveTab] = useState<Tab | 'callsigns'>('industry');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<{ industry: FilterIndustry[]; model: FilterModel[] }>({
    industry: [],
    model: [],
  });

  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  // The industry whose company assignments are being managed (view/move/delete).
  const [assignIndustry, setAssignIndustry] = useState<FilterIndustry | null>(null);
  // The model whose units are being viewed (read-only — a unit's model is its
  // identity, so units aren't re-homed here; see ModelUnitsDrawer / ISS-037).
  const [unitsModel, setUnitsModel] = useState<FilterModel | null>(null);
  // Form fields (a superset; each form uses the ones it needs).
  const [fName, setFName] = useState('');
  const [fSlug, setFSlug] = useState('');
  const [fCode, setFCode] = useState('');
  const [fLabel, setFLabel] = useState('');
  const [fOrder, setFOrder] = useState('');
  const [fCallsigns, setFCallsigns] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ind, mod] = await Promise.all([getFilterIndustries(), getFilterModels()]);
      setData({ industry: ind.industries, model: mod.models });
    } catch (error) {
      console.error('Failed to load filters:', error);
      toast.error('Failed to load metadata');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = (tab: Tab) => {
    setFName(''); setFSlug(''); setFCode(''); setFLabel(''); setFOrder(''); setFCallsigns('');
    setDrawer({ mode: 'create', tab });
  };

  const openEdit = (tab: Tab, item: FilterItem) => {
    if (tab === 'industry') {
      const i = item as FilterIndustry;
      setFName(i.name); setFSlug(i.slug || '');
    } else {
      const m = item as FilterModel;
      setFCode(m.code || ''); setFLabel(m.label || ''); setFCallsigns(formatCallsigns(m.callsigns));
    }
    setFOrder(item.display_order != null ? String(item.display_order) : '');
    setDrawer({ mode: 'edit', tab, item });
  };

  const closeDrawer = () => setDrawer(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!drawer) return;
    const order = fOrder.trim() === '' ? undefined : Number(fOrder);
    if (order !== undefined && !Number.isFinite(order)) {
      toast.error('Order must be a number');
      return;
    }

    setSaving(true);
    try {
      if (drawer.tab === 'industry') {
        if (drawer.mode === 'create') {
          const slug = fSlug.trim();
          if (!SLUG_RE.test(slug)) {
            toast.error('Slug must be lowercase letters, numbers, and single hyphens');
            setSaving(false);
            return;
          }
          await createFilterIndustry({ name: fName.trim(), slug });
          toast.success('Industry created');
        } else {
          await updateFilterIndustry(drawer.item!.id, { name: fName.trim(), display_order: order });
          toast.success('Industry updated');
        }
      } else {
        const callsigns = parseCallsigns(fCallsigns);
        const callsignError = callsignProblem(callsigns);
        if (callsignError) {
          toast.error(callsignError);
          setSaving(false);
          return;
        }
        if (drawer.mode === 'create') {
          const code = fCode.trim().toUpperCase();
          if (!CODE_RE.test(code)) {
            toast.error('Code must be 2–6 uppercase letters or digits (e.g. 1RE, WEA)');
            setSaving(false);
            return;
          }
          await createFilterModel({ code, label: fLabel.trim(), callsigns });
          toast.success('Model created');
        } else {
          await updateFilterModel(drawer.item!.id, { label: fLabel.trim(), display_order: order, callsigns });
          toast.success('Model updated');
        }
      }
      closeDrawer();
      loadData();
    } catch (err) {
      // Backend messages are descriptive (duplicate code/slug, validation, etc.).
      toast.error(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Industries can be deleted only at ZERO assignments. If any company is still
  // assigned, open the assignments drawer so the user can see exactly which ones
  // and move them off first; only a truly-empty industry deletes directly. (The
  // server enforces the same guard as a backstop.)
  const handleDeleteIndustry = async (item: FilterItem) => {
    if ((item.count || 0) > 0) {
      setAssignIndustry(item as FilterIndustry);
      return;
    }
    if (!confirm(`Delete "${item.name}"? This can't be undone.`)) return;
    try {
      await deleteFilterIndustry(item.id);
      toast.success('Industry deleted');
      loadData();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete industry');
    }
  };

  const setActive = async (tab: Tab, item: FilterItem, isActive: boolean) => {
    try {
      if (tab === 'industry') await updateFilterIndustry(item.id, { is_active: isActive });
      else await updateFilterModel(item.id, { is_active: isActive });
      toast.success(isActive ? 'Reactivated' : 'Deactivated');
      loadData();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed');
    }
  };

  const statusColumn: Column<FilterItem> = {
    key: 'is_active',
    label: 'STATUS',
    width: '110px',
    align: 'center',
    render: (item) => item.is_active === false
      ? <Badge variant="secondary" className="text-muted-foreground">Retired</Badge>
      : <Badge variant="outline" className="border-[#8FB560]/40 text-[#8FB560]">Active</Badge>,
  };

  const baseColumns = (usageLabel: string): Column<FilterItem>[] => [
    { key: 'name', label: 'NAME', sortable: true, width: '100%' },
    { key: 'count', label: usageLabel, width: '110px', align: 'center', render: (item) => item.count || 0 },
    statusColumn,
    { key: 'created_at', label: 'CREATED', width: '120px', align: 'center', render: (item) => new Date(item.created_at).toLocaleDateString() },
  ];

  // Industries: the company count is a link into the assignments drawer so you
  // can see exactly which companies hold it and move them.
  const industryColumns: Column<FilterItem>[] = baseColumns('COMPANIES').map((col) =>
    col.key === 'count'
      ? {
          ...col,
          render: (item) => {
            const n = item.count || 0;
            return n > 0 ? (
              <button
                type="button"
                onClick={() => setAssignIndustry(item as FilterIndustry)}
                className="tabular-nums text-brand underline-offset-2 hover:underline"
                title="View & reassign the companies using this industry"
              >
                {n}
              </button>
            ) : (
              <span className="tabular-nums text-muted-foreground">0</span>
            );
          },
        }
      : col,
  );

  // Models: the unit count links into a READ-ONLY units panel (a unit's model is
  // its identity, so no reassign here — see ModelUnitsDrawer / ISS-037).
  const modelColumns: Column<FilterItem>[] = baseColumns('UNITS').map((col) =>
    col.key === 'count'
      ? {
          ...col,
          render: (item) => {
            const n = item.count || 0;
            return n > 0 ? (
              <button
                type="button"
                onClick={() => setUnitsModel(item as FilterModel)}
                className="tabular-nums text-brand underline-offset-2 hover:underline"
                title="View the units on this model"
              >
                {n}
              </button>
            ) : (
              <span className="tabular-nums text-muted-foreground">0</span>
            );
          },
        }
      : col,
  );

  const industryActions: Action<FilterItem>[] = [
    { label: 'Edit', icon: Pencil, onClick: (item) => openEdit('industry', item) },
    { label: 'Deactivate', icon: Archive, onClick: (item) => setActive('industry', item, false), show: (item) => item.is_active !== false },
    { label: 'Reactivate', icon: ArchiveRestore, onClick: (item) => setActive('industry', item, true), show: (item) => item.is_active === false },
    { label: 'Delete', icon: Trash2, variant: 'destructive', onClick: handleDeleteIndustry },
  ];

  // Models: no hard-delete yet — a model code is cross-repo pricing vocabulary and
  // delete is gated on a Sales-side reference check (ISS-037). Retire instead.
  const modelActions: Action<FilterItem>[] = [
    { label: 'Edit', icon: Pencil, onClick: (item) => openEdit('model', item) },
    { label: 'Deactivate', icon: Archive, onClick: (item) => setActive('model', item, false), show: (item) => item.is_active !== false },
    { label: 'Reactivate', icon: ArchiveRestore, onClick: (item) => setActive('model', item, true), show: (item) => item.is_active === false },
  ];

  const industryBulkActions: BulkAction<FilterItem>[] = [
    {
      label: 'Delete Selected',
      icon: Trash2,
      variant: 'destructive',
      onClick: async (items: FilterItem[]) => {
        if (!confirm(`Delete ${items.length} industries? Any still assigned to a company will be skipped.`)) return;
        const results = await Promise.allSettled(items.map((item) => deleteFilterIndustry(item.id)));
        loadData();
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed === 0) toast.success(`Deleted ${items.length} industries`);
        else toast.error(`Deleted ${items.length - failed} of ${items.length}; ${failed} still in use`);
      },
    },
  ];

  const modelBulkActions: BulkAction<FilterItem>[] = [
    {
      label: 'Retire Selected',
      icon: Archive,
      onClick: async (items: FilterItem[]) => {
        if (!confirm(`Retire ${items.length} models? They stay resolvable (units + pricing) but drop out of active filters.`)) return;
        const results = await Promise.allSettled(items.map((item) => updateFilterModel(item.id, { is_active: false })));
        loadData();
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed === 0) toast.success(`Retired ${items.length} models`);
        else toast.error(`Retired ${items.length - failed} of ${items.length}; ${failed} failed`);
      },
    },
  ];

  const isIndustry = drawer?.tab === 'industry';
  const drawerTitle = drawer
    ? `${drawer.mode === 'create' ? 'Add' : 'Edit'} ${isIndustry ? 'Industry' : 'Model'}`
    : '';

  return (
    <div className="space-y-6 sm:space-y-8 flex-1 flex flex-col min-h-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0">
        <div className="flex-shrink-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-white whitespace-nowrap">Metadata & Filters</h1>
          <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">Manage system-wide categories and classifications</p>
        </div>
        {activeTab !== 'callsigns' && (
          <Button onClick={() => openCreate(activeTab)} className="bg-[#8FB560] hover:bg-[#7a9d51] whitespace-nowrap w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Add {activeTab === 'industry' ? 'Industry' : 'Model'}
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as Tab | 'callsigns')} className="flex-1 flex flex-col min-h-0">
        <TabsList className="bg-card border border-border p-1 w-fit">
          <TabsTrigger value="industry" className="data-[state=active]:bg-brand data-[state=active]:text-brand-foreground gap-2">
            <Layers className="w-4 h-4" /> Industry
          </TabsTrigger>
          <TabsTrigger value="model" className="data-[state=active]:bg-brand data-[state=active]:text-brand-foreground gap-2">
            <Truck className="w-4 h-4" /> Model
          </TabsTrigger>
          <TabsTrigger value="callsigns" className="data-[state=active]:bg-brand data-[state=active]:text-brand-foreground gap-2">
            <Hash className="w-4 h-4" /> Callsigns
          </TabsTrigger>
        </TabsList>

        <TabsContent value="industry" className="mt-6">
          <AdminDataTable<FilterItem>
            data={data.industry}
            columns={industryColumns}
            loading={loading}
            emptyMessage="No industries found"
            actions={industryActions}
            bulkActions={industryBulkActions}
            tableId="filters-industry"
            minRows={10}
          />
        </TabsContent>

        <TabsContent value="model" className="mt-6">
          <AdminDataTable<FilterItem>
            data={data.model}
            columns={modelColumns}
            loading={loading}
            emptyMessage="No models found"
            actions={modelActions}
            bulkActions={modelBulkActions}
            tableId="filters-model"
            minRows={10}
          />
        </TabsContent>

        <TabsContent value="callsigns" className="mt-6">
          {activeTab === 'callsigns' && <CallsignsDirectory onShowModels={() => setActiveTab('model')} />}
        </TabsContent>
      </Tabs>

      <AdminFormDrawer open={drawer !== null} onClose={closeDrawer} title={drawerTitle}>
        {drawer && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {isIndustry ? (
              <>
                <div>
                  <Label>Name</Label>
                  <Input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="e.g. Live Events" required className="bg-background border-border" />
                </div>
                <div>
                  <Label>Slug</Label>
                  <Input
                    value={fSlug}
                    onChange={(e) => setFSlug(e.target.value)}
                    placeholder="e.g. live-events"
                    required
                    disabled={drawer.mode === 'edit'}
                    className="bg-background border-border font-mono"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {drawer.mode === 'edit'
                      ? 'Locked — companies are matched by this slug.'
                      : 'Lowercase letters, numbers, and single hyphens. Permanent once created.'}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Code</Label>
                  <Input
                    value={fCode}
                    onChange={(e) => setFCode(e.target.value.toUpperCase())}
                    placeholder="e.g. 1RE"
                    required
                    disabled={drawer.mode === 'edit'}
                    className="bg-background border-border font-mono"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {drawer.mode === 'edit'
                      ? 'Locked — units and Sales/Landing pricing resolve by this code.'
                      : '2–6 uppercase letters or digits. Permanent once created; a new code prices “—” until Sales adds a rate.'}
                  </p>
                </div>
                <div>
                  <Label>Label</Label>
                  <Input value={fLabel} onChange={(e) => setFLabel(e.target.value)} placeholder="e.g. 1 Room Talent" required className="bg-background border-border" />
                </div>
                <CallsignsField
                  id="model-callsigns"
                  value={fCallsigns}
                  onChange={setFCallsigns}
                  example="2RX"
                  help="Extra shorthand for this model, beside its code. Separate several with commas."
                />
              </>
            )}

            {drawer.mode === 'edit' && (
              <div>
                <Label>Display order</Label>
                <Input type="number" value={fOrder} onChange={(e) => setFOrder(e.target.value)} placeholder="0" className="bg-background border-border" />
              </div>
            )}

            <Button type="submit" disabled={saving} className="w-full bg-[#8FB560] hover:bg-[#7a9d51]">
              {saving ? 'Saving…' : drawer.mode === 'create' ? 'Create' : 'Save changes'}
            </Button>
          </form>
        )}
      </AdminFormDrawer>

      <IndustryAssignmentsDrawer
        industry={assignIndustry}
        allIndustries={data.industry}
        onClose={() => setAssignIndustry(null)}
        onChanged={loadData}
      />

      <ModelUnitsDrawer
        model={unitsModel}
        allModels={data.model}
        onClose={() => setUnitsModel(null)}
        onChanged={loadData}
      />
    </div>
  );
}
